import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { pathname } = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => setOpen(false), [pathname]);

  const navLinks = [
    { to: "/#how", label: "How it works" },
    { to: "/#workflows", label: "Workflows" },
    { to: "/#pricing", label: "Pricing" },
    { to: "/#faq", label: "FAQ" },
    { to: "/blog", label: "Insights" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-40 transition-colors ${
        scrolled
          ? "bg-[#FAF7F2]/90 backdrop-blur border-b border-[#E5E0D8]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-2.5 font-sans-tight font-extrabold tracking-tight text-[#1A1A1A] text-2xl"
        >
          <LogoMark size={30} />
          <span>LeadRiver</span>
        </Link>

        <nav className="hidden md:flex items-center gap-9 font-sans-tight text-[17px] font-bold text-[#1A1A1A]">
          {navLinks.map((l) => (
            <a
              key={l.to}
              href={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="hover:text-[#0B3D5C] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href="/#founding" data-testid="navbar-cta" className="btn btn-primary">
            Claim founding spot
          </a>
        </div>

        <button
          className="md:hidden p-2 -mr-2 text-[#1A1A1A]"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-[#E5E0D8] bg-[#FAF7F2]"
          data-testid="mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4 font-sans-tight">
            {navLinks.map((l) => (
              <a
                key={l.to}
                href={l.to}
                data-testid={`mobile-nav-${l.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                className="text-[#1A1A1A]"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/#founding"
              className="btn btn-primary mt-2"
              data-testid="mobile-cta"
            >
              Claim founding spot
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

export function LogoMark({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden
    >
      <path
        d="M3 22 C10 14, 14 28, 22 18 C26 13, 28 10, 29 8"
        stroke="#0B3D5C"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="29" cy="8" r="3" fill="#E07B3A" />
    </svg>
  );
}
